import type { ResultValue } from './api';

/**
 * Demo semantic model + dataset (§6c). There is no persistence layer yet, so the
 * dashboard ships a small governed model and matching rows and sends both with
 * every question. This exercises the real query API — planner, guardrails,
 * executor — end to end; a production build swaps this for a model loaded per
 * tenant and data served from the connector.
 */

const field = (
  technical_name: string,
  over: Record<string, unknown> = {},
): Record<string, unknown> => ({
  technical_name,
  business_name: technical_name,
  data_type: 'string',
  description: '',
  example_values: [],
  nullable: true,
  sensitivity: 'Internal',
  is_measure_candidate: false,
  is_dimension_candidate: false,
  date_role: null,
  assumption: false,
  ambiguous: false,
  ...over,
});

export const demoModel = {
  meta: {
    generated_at: '2026-01-01T00:00:00.000Z',
    source_artifacts: ['petstore.json'],
    tenant_scope: 'multi',
    version: '3.0.0',
    assumptions: [],
    ambiguities: [],
    confidence: {
      score: 86,
      band: 'High',
      signals: [
        { name: 'enrichment_coverage', score: 90, weight: 0.3, detail: 'Most fields AI-named with confidence.' },
        { name: 'relationship_integrity', score: 100, weight: 0.25, detail: 'All relationships fully resolved.' },
        { name: 'clarity', score: 80, weight: 0.25, detail: '1 unresolved ambiguity.' },
        { name: 'governance_risk', score: 70, weight: 0.2, detail: 'Some sensitive fields flagged.' },
      ],
    },
  },
  entities: [
    {
      technical_name: 'Pet',
      business_name: 'Pet',
      description: 'An animal available in the store.',
      primary_identifier: 'id',
      display_field: 'name',
      fields: [
        field('id', { data_type: 'integer' }),
        field('name', { data_type: 'string' }),
        field('status', { is_dimension_candidate: true }),
        field('weight', { data_type: 'number', is_measure_candidate: true }),
        field('category_id', { data_type: 'integer', is_dimension_candidate: true }),
        field('created_at', { data_type: 'datetime', date_role: 'Transaction' }),
      ],
    },
    {
      technical_name: 'Category',
      business_name: 'Category',
      description: 'A grouping of pets.',
      primary_identifier: 'id',
      display_field: 'name',
      fields: [field('id', { data_type: 'integer' }), field('name', { data_type: 'string' })],
    },
  ],
  dimensions: [],
  metrics: [],
  kpis: [
    { name: 'Pet Count', formula: 'COUNT(Pet.id)', purpose: 'Total number of pets.', visualization: 'number' },
    { name: 'Category Count', formula: 'COUNT(Category.id)', purpose: 'Total number of categories.', visualization: 'number' },
    { name: 'Average Weight', formula: 'AVG(Pet.weight)', purpose: 'Average pet weight (kg).', visualization: 'number' },
  ],
  relationships: [
    { parent: 'Category', child: 'Pet', cardinality: '1:N', join_fields: ['category_id'], description: 'Each Category has many Pet.' },
  ],
  business_glossary: [],
  status_enums: [
    { field: 'Pet.status', technical_value: 'available', business_meaning: 'Available', display_label: 'Available' },
    { field: 'Pet.status', technical_value: 'pending', business_meaning: 'Pending', display_label: 'Pending' },
    { field: 'Pet.status', technical_value: 'sold', business_meaning: 'Sold', display_label: 'Sold' },
  ],
  data_classification: [],
  reporting_risks: [],
};

type Row = Record<string, ResultValue>;

/** 16 pets spread across statuses, categories, and three months. */
const pets: Row[] = [
  { id: 1, name: 'Rex', status: 'available', weight: 12, category_id: 1, created_at: '2026-01-04T00:00:00Z' },
  { id: 2, name: 'Milo', status: 'available', weight: 8, category_id: 2, created_at: '2026-01-11T00:00:00Z' },
  { id: 3, name: 'Bella', status: 'sold', weight: 15, category_id: 1, created_at: '2026-01-19T00:00:00Z' },
  { id: 4, name: 'Luna', status: 'pending', weight: 6, category_id: 2, created_at: '2026-01-24T00:00:00Z' },
  { id: 5, name: 'Coco', status: 'available', weight: 9, category_id: 1, created_at: '2026-02-02T00:00:00Z' },
  { id: 6, name: 'Max', status: 'sold', weight: 20, category_id: 3, created_at: '2026-02-06T00:00:00Z' },
  { id: 7, name: 'Ruby', status: 'available', weight: 7, category_id: 2, created_at: '2026-02-13T00:00:00Z' },
  { id: 8, name: 'Ollie', status: 'pending', weight: 11, category_id: 1, created_at: '2026-02-18T00:00:00Z' },
  { id: 9, name: 'Nala', status: 'sold', weight: 14, category_id: 3, created_at: '2026-02-25T00:00:00Z' },
  { id: 10, name: 'Leo', status: 'available', weight: 10, category_id: 1, created_at: '2026-03-03T00:00:00Z' },
  { id: 11, name: 'Daisy', status: 'available', weight: 5, category_id: 2, created_at: '2026-03-09T00:00:00Z' },
  { id: 12, name: 'Rocky', status: 'sold', weight: 18, category_id: 3, created_at: '2026-03-14T00:00:00Z' },
  { id: 13, name: 'Lily', status: 'pending', weight: 8, category_id: 1, created_at: '2026-03-20T00:00:00Z' },
  { id: 14, name: 'Toby', status: 'available', weight: 13, category_id: 2, created_at: '2026-03-26T00:00:00Z' },
  { id: 15, name: 'Zoe', status: 'available', weight: 9, category_id: 3, created_at: '2026-03-28T00:00:00Z' },
  { id: 16, name: 'Buddy', status: 'sold', weight: 16, category_id: 1, created_at: '2026-03-30T00:00:00Z' },
];

const categories: Row[] = [
  { id: 1, name: 'Dogs' },
  { id: 2, name: 'Cats' },
  { id: 3, name: 'Rabbits' },
];

export const demoDataset: Record<string, Row[]> = { Pet: pets, Category: categories };

/** Suggested questions shown as chips under the chat input. */
export const sampleQuestions = [
  'How many pets by status?',
  'Show pets over time',
  'Average weight by status',
  'How many pets are there?',
];

/** KPI tiles fire these questions on load; the deterministic planner resolves them. */
export const kpiQuestions: { label: string; question: string; unit?: string }[] = [
  { label: 'Total Pets', question: 'how many pets' },
  { label: 'Categories', question: 'how many categories' },
  { label: 'Avg Weight', question: 'average weight', unit: 'kg' },
];

/**
 * The OpenAPI spec the dashboard POSTs to `/models` to generate a real,
 * pipeline-produced model on first load — closing the build → store → serve →
 * query loop. It mirrors the `demoModel` shape (plus a `weight` measure) so the
 * bundled `demoDataset` executes against the generated model.
 */
export const demoSpec = JSON.stringify({
  openapi: '3.0.3',
  info: { title: 'Petstore', version: '1.0.0' },
  paths: {},
  components: {
    schemas: {
      Pet: {
        type: 'object',
        required: ['id', 'name'],
        properties: {
          id: { type: 'integer', format: 'int64', example: 10 },
          name: { type: 'string', example: 'doggie' },
          status: { type: 'string', enum: ['available', 'pending', 'sold'], example: 'available' },
          weight: { type: 'number', format: 'double', example: 12 },
          category_id: { type: 'integer', format: 'int64', nullable: true },
          created_at: { type: 'string', format: 'date-time' },
        },
      },
      Category: {
        type: 'object',
        required: ['id'],
        properties: {
          id: { type: 'integer', format: 'int64', example: 1 },
          name: { type: 'string', example: 'Dogs' },
        },
      },
    },
  },
});
